import { useCallback, useEffect, useState } from 'react'
import {
  appConfigResponseSchema,
  SAMPLE_OFFER_TYPE_LABEL,
  SAMPLE_PORTAL_COPY,
  type FactorySampleOfferInput,
  type FactorySampleOrderView,
  type Language,
  type SampleMessageView,
  type SampleOfferView,
  SAMPLE_MESSAGE_STATUS_LABEL,
} from '@repo/shared'
import { env } from '../env'
import { useGetToken } from '../lib/auth'
import { samplesApi, saveBlob } from '../samples-api'
import { SampleOfferForm, SampleOrderCard } from './sample-forms'

/**
 * 서버가 샘플 경로를 갖췄고(features.sampleBroker) 관리자 스위치가 켜졌는지, 그리고 AI 중계 대화(features.sampleMessages +
 * settings.sampleBroker.messagesEnabled)도 켜졌는지. null = 아직 모름
 */
export function useSampleBrokerFeature() {
  const [enabled, setEnabled] = useState<{ samples: boolean; messages: boolean } | null>(null)
  useEffect(() => {
    let alive = true
    void fetch(`${env.API_URL}/config`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((raw) => {
        const c = appConfigResponseSchema.parse(raw)
        const samples = c.features?.sampleBroker === true && c.settings.sampleBroker.enabled
        if (alive)
          setEnabled({
            samples,
            messages:
              samples &&
              c.features?.sampleMessages === true &&
              c.settings.sampleBroker.messagesEnabled,
          })
      })
      .catch(() => {
        if (alive) setEnabled({ samples: false, messages: false })
      })
    return () => {
      alive = false
    }
  }, [])
  return enabled
}

/**
 * 공장 포털의 샘플·시제품 (AI 중계 샘플). 공장 계정(제조 협력 공장 계정 연결)으로 자기 제안을 올리고,
 * 들어온 주문의 사양서를 내려받고, 발송·일정을 한 번에 기록한다
 */
export function FactorySamplesSection({
  language,
  messages,
}: {
  language: Language
  /** AI 중계 대화가 켜졌는지 */
  messages: boolean
}) {
  const getToken = useGetToken()
  const t = SAMPLE_PORTAL_COPY[language]
  const [offers, setOffers] = useState<SampleOfferView[] | null>(null)
  const [orders, setOrders] = useState<FactorySampleOrderView[] | null>(null)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([
        samplesApi.factoryOffers(getToken),
        samplesApi.factoryOrders(getToken),
      ])
      setOffers(o.items)
      setOrders(r.items)
    } catch {
      setMessage(t.failed)
    }
  }, [getToken, t.failed])
  useEffect(() => {
    void load()
  }, [load])

  const act = async (action: () => Promise<unknown>, done?: string) => {
    setBusy(true)
    setMessage(null)
    try {
      await action()
      await load()
      if (done) setMessage(done)
    } catch {
      setMessage(t.failed)
    } finally {
      setBusy(false)
    }
  }
  const save = (input: FactorySampleOfferInput, id?: string) =>
    act(async () => {
      await samplesApi.factorySaveOffer(getToken, input, id)
      setEditing(null)
    }, t.saved)

  return (
    <section className="space-y-4">
      <h2 className="text-xl font-bold">{t.section}</h2>
      <p className="text-sm">{t.scope}</p>
      {message ? <p role="status">{message}</p> : null}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t.offersTitle}</h3>
          <button type="button" className="underline" onClick={() => setEditing('new')}>
            {t.newOffer}
          </button>
        </div>
        {editing === 'new' ? (
          <SampleOfferForm lang={language} offer={null} disabled={busy} onSave={(i) => save(i)} />
        ) : null}
        {offers?.map((offer) =>
          editing === offer.id ? (
            <SampleOfferForm
              key={offer.id}
              lang={language}
              offer={offer}
              disabled={busy}
              onSave={(i) => save(i, offer.id)}
            />
          ) : (
            <div
              key={offer.id}
              className="flex items-center justify-between rounded border border-gray-200 p-3 text-sm"
            >
              <span>
                {SAMPLE_OFFER_TYPE_LABEL[language][offer.offerType]} · {t.offerStatus[offer.status]}
                {offer.terms.price !== null
                  ? ` · ${offer.terms.currency} ${offer.terms.price}`
                  : ''}
              </span>
              <button type="button" className="underline" onClick={() => setEditing(offer.id)}>
                {t.edit}
              </button>
            </div>
          ),
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{t.ordersTitle}</h3>
        {orders && !orders.length ? <p>{t.noOrders}</p> : null}
        {orders?.map((order) => (
          <div key={order.id} className="space-y-2">
            <SampleOrderCard
              key={order.id}
              lang={language}
              order={order}
              disabled={busy}
              onProgress={(input) =>
                act(() => samplesApi.factoryProgress(getToken, order.id, input))
              }
              onCsv={() =>
                void act(async () =>
                  saveBlob(
                    await samplesApi.factorySpecCsv(getToken, order.id),
                    `recipecat-spec-${order.customerRef}.csv`,
                  ),
                )
              }
            />
            {messages && order.spec ? (
              <FactoryThread language={language} orderId={order.id} />
            ) : null}
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * 주문 하나의 고객 대화 (AI 중계 메시지). 내 글은 상태와 함께, 고객 글은 AI 가 정리한 요청과 반영할 점만.
 * 역제안은 체크 하나로 보낸다
 */
function FactoryThread({ language, orderId }: { language: Language; orderId: string }) {
  const getToken = useGetToken()
  const t = SAMPLE_PORTAL_COPY[language]
  const [items, setItems] = useState<SampleMessageView[]>([])
  const [text, setText] = useState('')
  const [counter, setCounter] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    samplesApi
      .factoryMessages(getToken, orderId)
      .then((r) => setItems(r.items))
      .catch(() => setFailed(true))
  }, [getToken, orderId])
  const send = async () => {
    setBusy(true)
    setFailed(false)
    try {
      const r = await samplesApi.factorySendMessage(getToken, orderId, {
        kind: counter ? 'counter_offer' : 'message',
        text,
      })
      setItems(r.items)
      setText('')
      setCounter(false)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-2 rounded border border-gray-200 p-4 text-sm">
      <h4 className="font-semibold">{t.messagesTitle}</h4>
      <p className="text-gray-500">{t.messagesNote}</p>
      {items.map((m) =>
        m.mine ? (
          <p key={m.id} className="ml-8 rounded bg-gray-100 p-2">
            {m.sentText}{' '}
            <span className="text-gray-500">
              · {SAMPLE_MESSAGE_STATUS_LABEL[language][m.status]}
            </span>
          </p>
        ) : m.relayed ? (
          <div key={m.id} className="mr-8 rounded border border-gray-200 p-2">
            <p>{m.relayed.tuned}</p>
            {m.relayed.changes.length ? (
              <p className="text-gray-500">
                {t.changes}: {m.relayed.changes.join(' · ')}
              </p>
            ) : null}
          </div>
        ) : null,
      )}
      {failed ? <p role="alert">{t.failed}</p> : null}
      <textarea
        className="w-full rounded border border-gray-300 p-2"
        maxLength={2000}
        placeholder={t.messagePlaceholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={counter} onChange={(e) => setCounter(e.target.checked)} />
          {t.counterOffer}
        </label>
        <button
          type="button"
          className="rounded bg-gray-900 px-3 py-1.5 font-semibold text-white disabled:opacity-50"
          disabled={busy || !text.trim()}
          onClick={() => void send()}
        >
          {t.send}
        </button>
      </div>
    </div>
  )
}
